/** @format */

import React, { FunctionComponent, ReactElement, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import GridContainer from '../../../../../components/Grid/GridContainer';
import GridItem from '../../../../../components/Grid/GridItem';
import {
    SimCardServiceItemConfiguration,
    SimCardServiceTypeConfigurationGet,
    SimCardServiceTypeConfigurationPost,
    SimCardServiceTypeConfigurationPut,
    SimCardServiceTypeItem,
} from '../../../../../interfaces/collections/SimCardServiceInterface';
import I18n from '../../../../../components/Translate/I18n';
import CustomTextButtom from '../../../../../components/Buttons/CustomTextButton';
import { SimpleSelect } from '../../../../../components/Adescom/SimpleSelect';
import { getBooleanElementList } from '../../../../lib/search/listTableHeader/HeaderBooleanCheckBoxSelectList';
import {
    PARAM_070X_BARRING_OFF,
    PARAM_070X_BARRING_ON,
    PARAM_080X_BARRING_OFF,
    PARAM_080X_BARRING_ON,
    PARAM_7XXX_BARRING_OFF,
    PARAM_7XXX_BARRING_ON,
} from '../../../../../modules/features/SmsRequestTemplateType';
import { ItemLoaders } from '../../../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../../../helpers/FormGeneral';
import CustomInputExt from '../../../../../components/CustomInput/CustomInputExt';

type OplSimCardServiceTypeServiceBosSmsMsgEditType = {
    open: boolean;
    reload: (data: SimCardServiceTypeConfigurationGet) => void;
    simCardServiceType: SimCardServiceTypeItem;
    close: () => void;
    languageName: string;
    simCardServiceTypeConfiguration: SimCardServiceTypeConfigurationGet | SimCardServiceTypeConfigurationPost | SimCardServiceTypeConfigurationPut;
    isPost: boolean;
    id?: string | null;
    languageId?: number | null;
    platformResellerId: number;
};

export const OplSimCardServiceTypeServiceBosSmsMsgEdit: FunctionComponent<OplSimCardServiceTypeServiceBosSmsMsgEditType> = ({
    open,
    reload,
    simCardServiceType,
    close,
    simCardServiceTypeConfiguration,
    languageName,
    isPost,
    id,
    languageId,
    platformResellerId,
}): ReactElement => {
    const dispatch = useDispatch();
    const [configuration, setConfiguration] = useState<SimCardServiceItemConfiguration>(JSON.parse(simCardServiceType.configuration));
    const save = (): void => {
        simCardServiceTypeConfiguration.configuration = JSON.stringify(configuration);
        const itemLoader = new ItemLoaders(dispatch);
        if (!isPost) {
            itemLoader.putWithCallback(
                FormGeneralHelper.getByUri(id ?? 'empty'),
                'no-dispatch',
                simCardServiceTypeConfiguration,
                (data: SimCardServiceTypeConfigurationGet): void => {
                    reload(data);
                    close();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        } else {
            const simCardServiceTypeConfigurationPost: SimCardServiceTypeConfigurationPost = {
                configuration: simCardServiceTypeConfiguration.configuration,
                simCardServiceType: '/base-api/api/platform/sim_card_service_types/' + simCardServiceType.id.toString(),
                systemLanguage: '/base-api/api/platform/system_languages/' + (languageId ?? -1).toString(),
                platformReseller: '/base-api/api/platform/platform_resellers/' + platformResellerId.toString(),
            };
            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_type_configurations')),
                'no-dispatch',
                simCardServiceTypeConfigurationPost,
                (data: SimCardServiceTypeConfigurationGet): void => {
                    reload(data);
                    close();
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        setConfiguration({ ...configuration, [field]: selected === 1 });
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        setConfiguration({ ...configuration, [field]: value });
    };

    return (
        <Dialog open={open} maxWidth="md" onClose={close} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle>
                <I18n trans="prov:feature.sms_message" />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.service_bos_7XXX_BARRING_on"
                            fieldName={PARAM_7XXX_BARRING_ON}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PARAM_7XXX_BARRING_ON ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PARAM_7XXX_BARRING_ON_MSG"
                            value={configuration.PARAM_7XXX_BARRING_ON_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.service_bos_7XXX_BARRING_off"
                            fieldName={PARAM_7XXX_BARRING_OFF}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PARAM_7XXX_BARRING_OFF ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PARAM_7XXX_BARRING_OFF_MSG"
                            value={configuration.PARAM_7XXX_BARRING_OFF_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.service_bos_080X_BARRING_on"
                            fieldName={PARAM_080X_BARRING_ON}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PARAM_080X_BARRING_ON ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PARAM_080X_BARRING_ON_MSG"
                            value={configuration.PARAM_080X_BARRING_ON_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.service_bos_080X_BARRING_off"
                            fieldName={PARAM_080X_BARRING_OFF}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PARAM_080X_BARRING_OFF ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PARAM_080X_BARRING_OFF_MSG"
                            value={configuration.PARAM_080X_BARRING_OFF_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>

                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.service_bos_070X_BARRING_on"
                            fieldName={PARAM_070X_BARRING_ON}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PARAM_070X_BARRING_ON ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PARAM_070X_BARRING_ON_MSG"
                            value={configuration.PARAM_070X_BARRING_ON_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <SimpleSelect
                            label="prov:feature.service_bos_070X_BARRING_off"
                            fieldName={PARAM_070X_BARRING_OFF}
                            required={true}
                            elements={getBooleanElementList()}
                            dispatchName={'SMS_MESSAGE'}
                            selectedValue={configuration.PARAM_070X_BARRING_OFF ? 1 : 2}
                            callback={onChangeSelectEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExt
                            formControlProps={{ fullWidth: true }}
                            required={true}
                            labelText={'prov:feature.package_sms'}
                            name="PARAM_070X_BARRING_OFF_MSG"
                            value={configuration.PARAM_070X_BARRING_OFF_MSG ?? ''}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom simple callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                <CustomTextButtom callback={save} callbackParam={{}} label="prov:feature.confirm_title" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
